import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, ArrowLeft, RefreshCw, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { PageShell } from '@/components/shared';
import { registrationsAPI } from '@/services/registrationsApi';
import { racesAPI } from '@/services/racesApi';
import { raceTypesAPI } from '@/services/raceTypesApi';
import { tshirtSizesAPI } from '@/services/tshirtSizesApi';
import { showError, showSuccess } from '@/utils/toastHelpers';

// All canonical field names supported by the import endpoint
const CANONICAL_FIELDS = [
  'FirstName', 'LastName', 'Email', 'DateOfBirth', 'Phone',
  'City', 'Country', 'Gender', 'RaceId', 'Race', 'RaceTypeId', 'RaceType',
  'BibNumber', 'TshirtSize', 'TeamName', 'DiscountCode', 'ParticipantType',
  'EmergencyContactPhone', 'RegistrationStatus', 'PaymentStatus',
];

// Fields that need value-level mapping
const VALUE_MAP_FIELDS = ['TshirtSize', 'RaceType'];

interface PreviewRow {
  rowIndex: number;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  city: string;
  country: string;
  raceType: string;
  tshirtSize: string;
  bibNumber: number | null;
  teamName: string;
  registrationStatus: string;
  paymentStatus: string;
  error: string | null;
}

interface DryRunResult {
  created: number;
  skipped: number;
  totalRows: number;
  errors: string[];
  csvColumns?: string[];
  csvUniqueValues?: Record<string, string[]>;
  dryRun?: boolean;
  previewRows?: PreviewRow[];
}

interface ColumnsResult {
  csvColumns: string[];
  csvUniqueValues: Record<string, string[]>;
  totalRows: number;
}

const AUTO_DETECT = '__auto__';

export default function CmsImportRegistrationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRaceId, setSelectedRaceId] = useState<string>('');

  // Step 1: columns discovery result
  const [columnsResult, setColumnsResult] = useState<ColumnsResult | null>(null);

  // Column mapping: canonical field → CSV column name (or AUTO_DETECT)
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});

  // Value mapping: field → { csvValue → targetValue }
  const [valueMap, setValueMap] = useState<Record<string, Record<string, string>>>({});

  // Dry-run result
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);

  // Rows excluded by the user (set of rowIndex values from previewRows)
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());

  // Load races + race types / t-shirt sizes scoped to the selected race (or all races when none selected)
  const { data: races = [] } = useQuery({
    queryKey: ['races'],
    queryFn: () => racesAPI.getRaces(),
  });

  // When a race is selected fetch only its race types; otherwise fetch for every race.
  const { data: allRaceTypes = [] } = useQuery<any[]>({
    queryKey: ['allRaceTypesFlat', selectedRaceId || (races as any[]).map((r: any) => r.id)],
    queryFn: async () => {
      if (selectedRaceId) {
        return raceTypesAPI.getRaceTypes(selectedRaceId).catch(() => []);
      }
      const results = await Promise.all(
        (races as any[]).map((r: any) => raceTypesAPI.getRaceTypes(r.id).catch(() => []))
      );
      return results.flat();
    },
    enabled: !!selectedRaceId || (races as any[]).length > 0,
  });

  // When a race is selected fetch only its t-shirt sizes; otherwise no list available.
  const { data: dbTshirtSizes = [] } = useQuery<any[]>({
    queryKey: ['tshirtSizesFlat', selectedRaceId],
    queryFn: () => tshirtSizesAPI.getTshirtSizes(selectedRaceId).catch(() => []),
    enabled: !!selectedRaceId,
  });

  // Derive unique RaceType names from DB (already scoped to selected race when one is chosen)
  const dbRaceTypeNames: string[] = [...new Set((allRaceTypes as any[]).map((rt: any) => rt.name as string))].sort();

  // Derive unique t-shirt size labels from DB
  const dbTshirtSizeLabels: string[] = [...new Set((dbTshirtSizes as any[]).map((ts: any) => ts.size as string))].sort();

  // When the file changes, auto-run columns discovery
  useEffect(() => {
    if (!file) return;
    runColumnsOnly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const runColumnsOnly = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setColumnsResult(null);
    setDryRunResult(null);
    try {
      const result: ColumnsResult = await registrationsAPI.importRegistrations(file, { columnsOnly: true, raceId: selectedRaceId || undefined });
      setColumnsResult(result);

      // Auto-initialise column mapping: if a CSV column name matches a canonical name exactly (case-insensitive), pre-select it
      const autoMap: Record<string, string> = {};
      CANONICAL_FIELDS.forEach((canon) => {
        const match = result.csvColumns.find((c) => c.toLowerCase() === canon.toLowerCase());
        autoMap[canon] = match ?? AUTO_DETECT;
      });
      setColumnMap(autoMap);

      // Auto-initialise value maps for value-map fields
      const autoValueMap: Record<string, Record<string, string>> = {};
      VALUE_MAP_FIELDS.forEach((field) => {
        const unique = result.csvUniqueValues?.[field] ?? [];
        if (unique.length > 0) {
          autoValueMap[field] = {};
          unique.forEach((v) => { autoValueMap[field][v] = v; });
        }
      });
      setValueMap(autoValueMap);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Error reading CSV file';
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [file]);

  const runDryRun = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    try {
      const effectiveColumnMap = buildEffectiveColumnMap();
      const effectiveValueMap = buildEffectiveValueMap();
      const result: DryRunResult = await registrationsAPI.importRegistrations(file, {
        dryRun: true,
        columnMap: effectiveColumnMap,
        valueMap: effectiveValueMap,
        raceId: selectedRaceId || undefined,
      });
      setDryRunResult(result);
      setExcludedRows(new Set());
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Dry run failed';
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [file, columnMap, valueMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const effectiveColumnMap = buildEffectiveColumnMap();
      const effectiveValueMap = buildEffectiveValueMap();
      const result = await registrationsAPI.importRegistrations(file, {
        dryRun: false,
        columnMap: effectiveColumnMap,
        valueMap: effectiveValueMap,
        raceId: selectedRaceId || undefined,
        excludeRows: excludedRows.size > 0 ? Array.from(excludedRows) : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ['registrations'] });
      showSuccess(`Импортирани ${result.created} регистрации.`);
      navigate('/cms/registrations');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Import failed';
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Build the columnMap param: only include entries where the user mapped something that differs from auto-detect
  function buildEffectiveColumnMap(): Record<string, string> {
    const result: Record<string, string> = {};
    if (!columnsResult) return result;
    Object.entries(columnMap).forEach(([canon, csvCol]) => {
      if (csvCol === AUTO_DETECT || !csvCol) return;
      // Only add if the CSV column name doesn't already equal the canonical name (case-insensitive)
      if (csvCol.toLowerCase() !== canon.toLowerCase()) {
        result[csvCol] = canon;
      }
    });
    return result;
  }

  function buildEffectiveValueMap(): Record<string, Record<string, string>> {
    const result: Record<string, Record<string, string>> = {};
    Object.entries(valueMap).forEach(([field, map]) => {
      const filtered: Record<string, string> = {};
      Object.entries(map).forEach(([csvVal, targetVal]) => {
        if (targetVal && targetVal !== csvVal) {
          filtered[csvVal] = targetVal;
        }
      });
      if (Object.keys(filtered).length > 0) result[field] = filtered;
    });
    return result;
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
    e.target.value = '';
  };

  const handleDropZoneClick = () => fileInputRef.current?.click();

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  return (
    <PageShell>
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/cms/registrations')}>
          <ArrowLeft className="w-4 h-4 mr-1" />Назад
        </Button>
        <h1 className="text-xl font-semibold text-foreground">Импорт на регистрации (CSV)</h1>
      </div>

      {/* Race selector */}
      <Card>
        <CardHeader><CardTitle className="text-base">Чекор 0 — Изберете трка</CardTitle></CardHeader>
        <CardContent>
          <select
            value={selectedRaceId}
            onChange={(e) => setSelectedRaceId(e.target.value)}
            className="w-full max-w-sm px-3 py-2 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">— изберете трка (или оставете во CSV) —</option>
            {(races as any[]).map((r: any) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          {selectedRaceId && (
            <p className="text-xs text-muted-foreground mt-1">
              Сите редови ќе се увезат во избраната трка. CSV не мора да содржи колона Race/RaceId.
            </p>
          )}
        </CardContent>
      </Card>

      {/* File pick */}
      <Card>
        <CardHeader><CardTitle className="text-base">Чекор 1 — Изберете CSV датотека</CardTitle></CardHeader>
        <CardContent>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileChange} />
          <div
            className="border-2 border-dashed border-input rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
            onClick={handleDropZoneClick}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileText className="w-8 h-8 text-primary" />
                <span className="font-medium text-foreground">{file.name}</span>
                <span className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB — клик за замена</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Upload className="w-8 h-8" />
                <span className="font-medium">Кликнете или влечете CSV датотека овде</span>
                <span className="text-xs">Поддржани: .csv</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="text-center py-6 text-muted-foreground text-sm">Анализа на датотеката...</div>
      )}

      {/* Column mapping */}
      {columnsResult && !loading && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Чекор 2 — Мапирање на колони</CardTitle>
            <p className="text-sm text-muted-foreground">
              Пронајдени {columnsResult.csvColumns.length} колони, {columnsResult.totalRows} редови.
              За секое поле изберете која CSV колона го содржи тоа поле.
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left pb-2 pr-4 font-medium text-muted-foreground w-1/3">Поле (модел)</th>
                    <th className="text-left pb-2 font-medium text-muted-foreground">CSV колона</th>
                  </tr>
                </thead>
                <tbody>
                  {CANONICAL_FIELDS.map((canon) => {
                    const selectedCol = columnMap[canon] ?? AUTO_DETECT;
                    const isRequired = ['FirstName', 'LastName', 'Email', 'DateOfBirth', 'Race', 'RaceType'].includes(canon);
                    return (
                      <tr key={canon} className="border-b border-border/50 last:border-0">
                        <td className="py-2 pr-4 font-mono text-xs text-foreground whitespace-nowrap">
                          {canon}
                          {isRequired && <span className="text-destructive ml-1">*</span>}
                        </td>
                        <td className="py-2">
                          <select
                            value={selectedCol}
                            onChange={(e) => setColumnMap((prev) => ({ ...prev, [canon]: e.target.value }))}
                            className="w-full max-w-xs px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <option value={AUTO_DETECT}>— авто / нема —</option>
                            {columnsResult.csvColumns.map((col) => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Value mapping for TshirtSize and RaceType */}
      {columnsResult && !loading && VALUE_MAP_FIELDS.map((field) => {
        const uniqueVals = columnsResult.csvUniqueValues?.[field] ?? [];
        // also check if the mapped column for this field has unique values
        // The field name in csvUniqueValues matches what's in the CSV header (auto-detected canonical)
        if (uniqueVals.length === 0) return null;

        const dbOptions = field === 'RaceType' ? dbRaceTypeNames : (field === 'TshirtSize' ? dbTshirtSizeLabels : []);

        return (
          <Card key={field}>
            <CardHeader>
              <CardTitle className="text-base">
                Чекор 2б — Вредносно мапирање: {field}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Поврзете ги вредностите од CSV со вредностите во системот.
              </p>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left pb-2 pr-4 font-medium text-muted-foreground">Вредност во CSV</th>
                    <th className="text-left pb-2 font-medium text-muted-foreground">
                      {field === 'RaceType' ? 'Тип на трка во системот' : 'Маичка во системот'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {uniqueVals.map((csvVal) => {
                    const currentTarget = valueMap[field]?.[csvVal] ?? csvVal;
                    return (
                      <tr key={csvVal} className="border-b border-border/50 last:border-0">
                        <td className="py-2 pr-4 font-mono text-xs text-foreground">{csvVal}</td>
                        <td className="py-2">
                          {dbOptions.length > 0 ? (
                            <select
                              value={currentTarget}
                              onChange={(e) =>
                                setValueMap((prev) => ({
                                  ...prev,
                                  [field]: { ...(prev[field] ?? {}), [csvVal]: e.target.value },
                                }))
                              }
                              className="w-full max-w-xs px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                              <option value="">— не менувај —</option>
                              {dbOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={currentTarget}
                              onChange={(e) =>
                                setValueMap((prev) => ({
                                  ...prev,
                                  [field]: { ...(prev[field] ?? {}), [csvVal]: e.target.value },
                                }))
                              }
                              className="w-full max-w-xs px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        );
      })}

      {/* Dry-run action + results */}
      {columnsResult && !loading && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Чекор 3 — Пробен увоз (Dry Run)</CardTitle>
              <Button variant="outline" onClick={runDryRun} disabled={loading}>
                <RefreshCw className="w-4 h-4 mr-2" />Стартувај пробен увоз
              </Button>
            </div>
          </CardHeader>
          {dryRunResult && (
            <CardContent className="space-y-4">
              {/* Summary counters */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-md bg-muted p-3">
                  <div className="text-xl font-bold text-foreground">{dryRunResult.totalRows}</div>
                  <div className="text-xs text-muted-foreground">Вкупно редови</div>
                </div>
                <div className="rounded-md bg-green-50 p-3">
                  <div className="text-xl font-bold text-green-700">
                    {(dryRunResult.previewRows ?? []).filter(r => !r.error && !excludedRows.has(r.rowIndex)).length}
                  </div>
                  <div className="text-xs text-muted-foreground">Ќе се увезат</div>
                </div>
                <div className="rounded-md bg-red-50 p-3">
                  <div className="text-xl font-bold text-red-700">
                    {dryRunResult.skipped + excludedRows.size}
                  </div>
                  <div className="text-xs text-muted-foreground">Ќе се прескокнат</div>
                </div>
              </div>

              {/* Errors list */}
              {dryRunResult.errors.length > 0 && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1 max-h-32 overflow-y-auto">
                  <div className="flex items-center gap-1 font-medium text-destructive text-xs mb-1">
                    <AlertCircle className="w-3 h-3" />Грешки:
                  </div>
                  {dryRunResult.errors.map((e, i) => (
                    <div key={i} className="text-xs text-destructive">{e}</div>
                  ))}
                </div>
              )}

              {/* Preview table */}
              {(dryRunResult.previewRows ?? []).length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-foreground">Преглед на редови</p>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <button
                        className="underline hover:text-foreground"
                        onClick={() => setExcludedRows(new Set())}
                      >
                        Избери ги сите
                      </button>
                      <span>·</span>
                      <button
                        className="underline hover:text-foreground"
                        onClick={() =>
                          setExcludedRows(new Set((dryRunResult.previewRows ?? []).map(r => r.rowIndex)))
                        }
                      >
                        Откажи ги сите
                      </button>
                    </div>
                  </div>
                  <div className="overflow-auto max-h-96 rounded-md border border-border">
                    <table className="w-full text-xs min-w-[900px]">
                      <thead className="sticky top-0 bg-muted z-10">
                        <tr>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground w-8">✓</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">#</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Биб</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Ime</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Презиме</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Е-пошта</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Роден</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Пол</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Тип трка</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Маица</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Град</th>
                          <th className="px-2 py-2 text-left font-medium text-muted-foreground">Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dryRunResult.previewRows ?? []).map((row) => {
                          const isExcluded = excludedRows.has(row.rowIndex);
                          const hasError = !!row.error;
                          return (
                            <tr
                              key={row.rowIndex}
                              className={[
                                'border-t border-border/50',
                                isExcluded ? 'opacity-40 bg-muted/30' : hasError ? 'bg-red-50' : '',
                              ].join(' ')}
                            >
                              <td className="px-2 py-1.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={!isExcluded}
                                  onChange={(e) =>
                                    setExcludedRows((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.delete(row.rowIndex);
                                      else next.add(row.rowIndex);
                                      return next;
                                    })
                                  }
                                />
                              </td>
                              <td className="px-2 py-1.5 text-muted-foreground">{row.rowIndex}</td>
                              <td className="px-2 py-1.5 font-mono font-semibold">
                                {row.bibNumber ?? <span className="text-muted-foreground">—</span>}
                              </td>
                              <td className="px-2 py-1.5">{row.firstName}</td>
                              <td className="px-2 py-1.5">{row.lastName}</td>
                              <td className="px-2 py-1.5 text-muted-foreground">{row.email}</td>
                              <td className="px-2 py-1.5">{row.dateOfBirth}</td>
                              <td className="px-2 py-1.5">{row.gender}</td>
                              <td className="px-2 py-1.5">{row.raceType}</td>
                              <td className="px-2 py-1.5">{row.tshirtSize}</td>
                              <td className="px-2 py-1.5">{row.city}</td>
                              <td className="px-2 py-1.5">
                                {hasError ? (
                                  <span className="text-destructive font-medium" title={row.error!}>
                                    <AlertCircle className="w-3 h-3 inline mr-1" />Грешка
                                  </span>
                                ) : (
                                  <span className="text-green-700">
                                    <CheckCircle2 className="w-3 h-3 inline mr-1" />OK
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(dryRunResult.previewRows ?? []).filter(r => !r.error).length > 0 &&
                dryRunResult.errors.length === 0 && excludedRows.size === 0 && (
                <div className="flex items-center gap-2 text-green-700 text-sm">
                  <CheckCircle2 className="w-4 h-4" />Нема грешки — датотеката е подготвена за увоз.
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Confirm */}
      {dryRunResult && !loading && (
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/cms/registrations')}>Откажи</Button>
          <Button
            onClick={handleConfirmImport}
            disabled={loading || (dryRunResult.previewRows ?? []).filter(r => !r.error && !excludedRows.has(r.rowIndex)).length === 0}
          >
            <Upload className="w-4 h-4 mr-2" />
            Потврди увоз ({(dryRunResult.previewRows ?? []).filter(r => !r.error && !excludedRows.has(r.rowIndex)).length} записи)
          </Button>
        </div>
      )}
    </PageShell>
  );
}
